import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home, PanelLeftClose, PanelLeftOpen, type LucideIcon } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBoards } from '@/api/boards';
import { usePreference, type SidebarMode } from '@/api/preferences';
import { ClientModeSection } from '@/components/shell/ClientModeSection';
import { BookmarksSection } from '@/components/shell/Bookmarks';
import { MODULES, PLANNED_MODULES } from '@/lib/modules';
import {
  MODULE_MENUS, activeLinkFor, isNavLink, isView, moduleForPath,
  type ModuleId, type NavGroup, type NavLinkItem,
} from '@/lib/navigation';
import { ROUTES, dashboardPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * The left-hand navigation — one accordion section per live module. (Admin moved to the ⚙ Settings
 * page in the header on 2026-10-14.)
 * Rendered entirely from lib/navigation.ts (the same data as Home and the page search):
 *
 *   Module ▸            the module you're in opens on arrival; what happens when you leave
 *     Group ▸           depends on the user's `nav.sidebarMode` preference (below). Groups are
 *                       collapsible (closed by default) and follow the same rule as modules
 *       Page            plain links; the current page is highlighted
 *       Soon            roadmap items, muted
 *
 * Pre-filtered views are NOT here — each page shows its own in a "Views" dropdown (ViewsMenu,
 * rendered by PageHeader), which keeps the sidebar to pages only.
 * Expanded sections persist in `cosmos:nav:expanded`; "Collapse" shrinks the bar to an icon rail
 * (`cosmos:nav:rail`, also the `[` key) whose icons open a flyout of the same items.
 * Desktop renders it beside <main>; on mobile the header's menu button opens it as a sheet.
 *
 * `nav.sidebarMode` (Settings → Preferences, general.md §4.13):
 *   follow  the current module + group open on arrival and close when you leave; hand-opened ones stay
 *   keep    everything that opens stays open (and is saved) until you close it
 *   single  only the current module + group are open; opening another closes the rest (never saved,
 *           so switching back to another mode restores your saved sections)
 * The mobile sheet always behaves like `follow` (it closes on navigation anyway).
 */

const EXPANDED_KEY = 'cosmos:nav:expanded';
const RAIL_KEY = 'cosmos:nav:rail';

function load<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v) as T; } catch { return fallback; }
}
function store(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

/** The rail preference, shared by the sidebar and the `[` shortcut. */
export function useNavRail() {
  const [rail, setRailState] = useState<boolean>(() => load(RAIL_KEY, false));
  const setRail = (v: boolean) => { setRailState(v); store(RAIL_KEY, v); };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      setRailState((r) => { store(RAIL_KEY, !r); return !r; });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return [rail, setRail] as const;
}

/** A section of the sidebar: a live module. */
interface Section { id: string; label: string; icon: LucideIcon; groups: NavGroup[]; to: string }

function useSections(): Section[] {
  const boards = useBoards();
  // Starred dashboards are per user, so they're added here rather than in navigation.ts.
  const starred: NavGroup[] = (boards.data?.dashboards ?? []).filter((d) => d.favourite)
    .map((d) => ({ kind: 'link' as const, to: dashboardPath(d.id), label: d.name }))
    .reduce<NavGroup[]>((g, item) => { g[0].items.push(item); return g; }, [{ title: 'Starred', items: [] }])
    .filter((g) => g.items.length);
  const sections: Section[] = MODULES.filter((m) => m.status === 'live').map((m) => ({
    id: m.id, label: m.label, icon: m.icon, to: m.to,
    groups: [...(MODULE_MENUS[m.id as ModuleId] ?? []), ...(m.id === 'dashboards' ? starred : [])],
  }));
  // Admin isn't here any more: it's under the ⚙ (Settings) in the header (decided 2026-10-14).
  return sections;
}

const sectionForPath = (pathname: string) => moduleForPath(pathname);

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const k of a) if (!b.has(k)) return false;
  return true;
}

export function Sidebar({ mode = 'desktop', onNavigate }: {
  mode?: 'desktop' | 'sheet';
  /** Sheet mode: close after a link is followed. */
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const sections = useSections();
  const [rail, setRail] = useNavRail();
  const isRail = mode === 'desktop' && rail;
  const current = sectionForPath(pathname);
  // The group holding the current page, e.g. `group:psa:Tickets`.
  const currentGroup = (() => {
    const sec = sections.find((x) => x.id === current);
    if (!sec) return null;
    const link = activeIn(sec, pathname);
    const g = link ? sec.groups.find((x) => x.items.includes(link)) : groupByPrefix(sec, pathname);
    return g ? groupKey(sec.id, g.title) : null;
  })();

  const preferredMode = usePreference('nav.sidebarMode');
  const navMode: SidebarMode = mode === 'sheet' ? 'follow' : preferredMode;

  // Expanded keys: section ids, and `group:<section>:<title>` for groups (collapsed by default).
  const [expanded, setExpandedState] = useState<Set<string>>(() => new Set(load<string[]>(EXPANDED_KEY, [])));
  // The section + group opened automatically for the current route. Never persisted, so they close
  // when you leave (or reload elsewhere); toggling one by hand makes it a normal, saved choice.
  const autoOpened = useRef<Set<string>>(new Set());
  // One at a time is never saved: it's rebuilt from the route, and the saved set is kept for the other modes.
  const persist = (next: Set<string>) => {
    if (navMode !== 'single') store(EXPANDED_KEY, [...next].filter((k) => !autoOpened.current.has(k)));
  };
  const setExpanded = (next: Set<string>) => { setExpandedState(next); persist(next); };
  const toggle = (key: string) => {
    autoOpened.current.delete(key);
    setExpandedState((prev) => {
      let next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (navMode === 'single') {
        // Opening a module closes the others (keeping its own open groups); opening a group closes its siblings.
        const isGroup = key.startsWith('group:');
        next = new Set([...prev].filter((k) => isGroup
          ? !k.startsWith('group:') || sectionOfKey(k) !== sectionOfKey(key)
          : k.startsWith('group:') && sectionOfKey(k) === key));
        next.add(key);
      } else next.add(key);
      persist(next);
      return next;
    });
  };

  // Open the section + group you arrive in. What happens to them when you leave depends on the mode.
  const lastMode = useRef(navMode);
  useEffect(() => {
    const want = [current, currentGroup].filter((k): k is string => !!k);
    const modeChanged = lastMode.current !== navMode;
    lastMode.current = navMode;
    if (navMode === 'single') {
      autoOpened.current = new Set();
      setExpandedState((prev) => { const next = new Set(want); return sameSet(prev, next) ? prev : next; });
      return;
    }
    setExpandedState((prev) => {
      // Coming back from One at a time: start again from the saved sections.
      const next = new Set(modeChanged ? load<string[]>(EXPANDED_KEY, []) : prev);
      if (navMode === 'keep') {
        // Keep open: what opens for a route becomes a normal, saved choice.
        autoOpened.current = new Set();
        for (const k of want) next.add(k);
        store(EXPANDED_KEY, [...next]);
        return next;
      }
      // Follow me: close what was opened automatically for the last route, unless you opened it yourself.
      if (modeChanged) autoOpened.current = new Set();
      for (const k of autoOpened.current) if (!want.includes(k)) next.delete(k);
      const auto = new Set<string>();
      for (const k of want) {
        if (!next.has(k)) { next.add(k); auto.add(k); }
        else if (autoOpened.current.has(k)) auto.add(k);
      }
      autoOpened.current = auto;
      // Same contents → keep the old Set so React bails out (no extra render per navigation).
      return sameSet(prev, next) ? prev : next;
    });
  }, [current, currentGroup, navMode]);

  const anyOpen = sections.some((s) => expanded.has(s.id));

  return (
    <aside
      aria-label="Main navigation"
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-border bg-card',
        mode === 'desktop' && 'hidden md:flex',
        isRail ? 'w-14' : mode === 'sheet' ? 'w-72' : 'w-64',
      )}
    >
      <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden py-2', isRail ? 'px-2' : 'px-2.5')}>
        {isRail ? (
          <div className="flex flex-col items-center gap-1">
            <ClientModeSection rail />
            <RailLink to={ROUTES.home} label="Home" icon={Home} active={pathname === ROUTES.home} />
            <div className="my-1 h-px w-6 bg-divider" />
            {sections.map((s) => <RailSection key={s.id} section={s} active={current === s.id} pathname={pathname} />)}
          </div>
        ) : (
          <>
            <ClientModeSection onNavigate={onNavigate} />
            <Link to={ROUTES.home} onClick={onNavigate}
              className={cn(ROW, pathname === ROUTES.home ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
              <Home className="h-4 w-4 shrink-0" />Home
            </Link>
            <BookmarksSection rowClass={ROW} onNavigate={onNavigate} />
            <div className="mx-2 my-2 h-px bg-divider" />
            {sections.map((s) => (
              <SectionBlock key={s.id} section={s} open={expanded.has(s.id)} active={current === s.id} pathname={pathname}
                expanded={expanded} onToggle={toggle} onNavigate={onNavigate} />
            ))}
            {PLANNED_MODULES.length > 0 && (
              <div className="mt-3">
                <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/75">Coming soon</div>
                {PLANNED_MODULES.map((m) => (
                  <Link key={m.id} to={m.to} onClick={onNavigate} className={cn(ROW, 'text-muted-foreground/70 hover:bg-muted/60 hover:text-foreground')}>
                    <m.icon className="h-4 w-4 shrink-0" />{m.label}<SoonPill />
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </nav>

      {mode === 'desktop' && (
        <div className={cn('flex items-center border-t border-divider py-1.5', isRail ? 'justify-center' : 'justify-between px-2.5')}>
          {/* Expand all contradicts One at a time, so it's only offered there as Collapse all. */}
          {!isRail && (anyOpen || navMode !== 'single') && (
            <button type="button" onClick={() => setExpanded(anyOpen ? new Set() : new Set(sections.map((s) => s.id)))}
              className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted/60 hover:text-foreground">
              {anyOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
          <button type="button" onClick={() => setRail(!rail)} title={isRail ? 'Expand sidebar ( [ )' : 'Collapse to icons ( [ )'}
            aria-label={isRail ? 'Expand sidebar' : 'Collapse sidebar to icons'}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground">
            {isRail ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
      )}
    </aside>
  );
}

const groupKey = (section: string, title: string) => `group:${section}:${title}`;
/** The section a key belongs to: itself for a section, `<section>` for `group:<section>:<title>`. */
const sectionOfKey = (key: string) => key.startsWith('group:') ? key.split(':')[1] : key;

/**
 * Record pages that match no menu link (/psa/tickets/123, /psa/opportunities/9) still open the
 * right group: the one whose links share the most leading path segments (at least two) with the path.
 */
function groupByPrefix(section: Section, pathname: string): NavGroup | null {
  const segs = pathname.split('/').filter(Boolean);
  let best: { g: NavGroup; n: number } | null = null;
  for (const g of section.groups) for (const i of g.items) {
    if (!isNavLink(i) || isView(i)) continue;
    const ls = i.to.split('/').filter(Boolean);
    let n = 0;
    while (n < ls.length && n < segs.length && ls[n] === segs[n]) n++;
    if (n >= 2 && (!best || n > best.n)) best = { g, n };
  }
  return best?.g ?? null;
}

const ROW = 'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-[15px] transition-colors';

function SoonPill() {
  return <span className="ml-auto rounded-full bg-muted px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Soon</span>;
}

/** The current page within a section: the most specific matching link. */
function activeIn(section: Section, pathname: string): NavLinkItem | null {
  return activeLinkFor(pathname, section.groups);
}

function SectionBlock({ section: s, open, active, pathname, expanded, onToggle, onNavigate }: {
  section: Section;
  open: boolean;
  active: boolean;
  pathname: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onNavigate?: () => void;
}) {
  const current = active ? activeIn(s, pathname) : null;
  const bodyId = `nav-${s.id}`;
  return (
    <div className="mb-0.5">
      <button type="button" onClick={() => onToggle(s.id)} aria-expanded={open} aria-controls={bodyId}
        className={cn(ROW, 'font-medium', active ? 'text-foreground' : 'text-foreground/80 hover:bg-muted/60 hover:text-foreground')}>
        <s.icon className={cn('h-4 w-4 shrink-0', active ? 'text-brand' : 'text-muted-foreground')} />
        <span className="truncate">{s.label}</span>
        <ChevronRight className={cn('ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <div id={bodyId} className="mb-2 ml-[1.05rem] border-l border-divider pl-1.5 pt-0.5">
          {s.groups.map((g) => {
            // Views (query links) live on the page itself (ViewsMenu), not here.
            const items = g.items.filter((i) => !isNavLink(i) || !isView(i));
            if (!items.length) return null;
            const key = groupKey(s.id, g.title);
            const groupOpen = expanded.has(key);
            const holdsCurrent = !!current && items.includes(current);
            return (
              <div key={g.title} className="pt-0.5">
                <button type="button" onClick={() => onToggle(key)} aria-expanded={groupOpen}
                  className={cn('flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[14.5px] font-medium transition-colors hover:bg-muted/60',
                    holdsCurrent ? 'text-foreground' : 'text-foreground/75 hover:text-foreground')}>
                  <span className="truncate">{g.title}</span>
                  <ChevronRight className={cn('ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform', groupOpen && 'rotate-90')} />
                </button>
                {groupOpen && (
                  <div className="mb-1">
                    {items.map((i) => isNavLink(i) ? (
                      <Link key={i.to} to={i.to} onClick={onNavigate} aria-current={current?.to === i.to ? 'page' : undefined}
                        className={cn('block truncate rounded-md py-1.5 pl-5 pr-2 text-sm transition-colors',
                          current?.to === i.to ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
                        {i.label}
                      </Link>
                    ) : (
                      <div key={i.label} className="flex items-center gap-2 py-1.5 pl-5 pr-2 text-sm text-muted-foreground/70">{i.label}<SoonPill /></div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RailLink({ to, label, icon: Icon, active }: { to: string; label: string; icon: LucideIcon; active: boolean }) {
  return (
    <Link to={to} title={label} aria-label={label}
      className={cn('flex h-9 w-9 items-center justify-center rounded-md transition-colors', active ? 'bg-muted text-brand' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
      <Icon className="h-4 w-4" />
    </Link>
  );
}

/** Rail mode: the section icon opens a flyout with the same pages and views. */
function RailSection({ section: s, active, pathname }: { section: Section; active: boolean; pathname: string }) {
  const current = active ? activeIn(s, pathname) : null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger title={s.label} aria-label={s.label}
        className={cn('flex h-9 w-9 items-center justify-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/40 data-[state=open]:bg-muted',
          active ? 'bg-muted text-brand' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
        <s.icon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" sideOffset={10} className="max-h-[80vh] min-w-[220px] overflow-y-auto">
        <DropdownMenuLabel className="text-sm">{s.label}</DropdownMenuLabel>
        {s.groups.map((g, gi) => {
          const links = g.items.filter(isNavLink).filter((l) => !isView(l));
          if (!links.length) return null;
          return (
            <div key={g.title}>
              <DropdownMenuSeparator />
              <div className="px-2 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/75">{g.title}</div>
              {links.map((l) => (
                <DropdownMenuItem key={`${gi}:${l.to}`} asChild className="cursor-pointer text-sm">
                  <Link to={l.to} className={cn(current?.to === l.to && 'font-medium text-foreground')}>{l.label}</Link>
                </DropdownMenuItem>
              ))}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
